import React, { useCallback, useState } from "react";
import Cross from "@assets/svg/CrossIcon";
import {
  Modal,
  Pressable,
  View,
  StyleSheet,
  TouchableWithoutFeedback,
  FlatList,
} from "react-native";
import Text from "@components/CustomText";
import BagIcon from "@assets/svg/MyTasks/BagIcon";
import PolyBagIcon from "@assets/svg/MyTasks/PolyBagIcon";
import Image from "@components/common/Image";
import { getBagsItemsData } from "@services/api";
import { colors } from "@constants/theme";
import { useFocusEffect } from "@react-navigation/native";
import SkeletonLoaderList from "@components/SkeletonLoaderList";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import t from "@constants/localization";

const PATH = "screens/MyTask";

const renderItem = (li: any) => {
  if (li?.type === "BAGS") {
    return (
      <View key={li.type} style={styles.itemContainer}>
        <View>
          <View style={styles.imageContainer}>
            <BagIcon />
            <Text>{t(`${PATH}/bags`)}</Text>
          </View>
        </View>
        <View style={styles.detailsContainer}>
          <Text style={styles.itemName}>{li.title}</Text>
        </View>
      </View>
    );
  } else if (li?.type === "PLASTIC_BAG") {
    return (
      <View key={li.type} style={[styles.itemContainer, styles.plasticBagRow]}>
        <View style={styles.imageContainer}>
          <PolyBagIcon width={48} height={48} />
        </View>
        <View style={styles.detailsContainer}>
          <Text
            variant="body_text_4"
            style={[styles.itemName, styles.plasticBagTitle]}
          >
            {li.title}
          </Text>
          <Text variant="body_text_3" style={styles.itemQuantity}>
            {t(`${PATH}/quantity`)}
            <Text variant="body_text_4" style={styles.itemName}>
              {li.quantity || 0}
            </Text>
          </Text>
        </View>
      </View>
    );
  } else {
    return (
      <View key={li.type} style={styles.itemContainer}>
        <View style={styles.imageContainer}>
          <Image
            source={{ uri: li.product_image }}
            style={styles.itemImage}
            resizeMode="cover"
            alt="Product Image"
          />
        </View>
        <View style={styles.detailsContainer}>
          <Text variant="body_text_4" style={styles.itemName}>
            {li.title}
          </Text>
          <Text variant="body_text_3" style={styles.itemQuantity}>
            {t(`${PATH}/quantity`)}
            <Text variant="body_text_4" style={styles.itemName}>
              {li.quantity || 0}
            </Text>
          </Text>
          {li.type === "LARGE_ITEM_SHIPMENT" ||
            (li.type === "SMALL_ITEM_SHIPMENT" && (
              <Text variant="caption_2" style={styles.itemQuantity}>
                {li.id}
              </Text>
            ))}
        </View>
      </View>
    );
  }
};

const BagsItemsModal = ({
  selectedCard,
  showBagsLargeItemsModal,
  setShowBagsLargeItemsModal,
}: any) => {
  const cxName = selectedCard?.user_name || "";
  const routeName = selectedCard?.route_name || "";
  const insets = useSafeAreaInsets();
  const [listItems, setListItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await getBagsItemsData({
        order_ids: selectedCard?.order_ids,
        user_id: selectedCard?.user_id,
        plastic_bag_opted_in: selectedCard?.plastic_bag_opted_in,
      });
      setListItems(data?.items_list || []);
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  }, [
    selectedCard?.order_ids,
    selectedCard?.user_id,
    selectedCard?.plastic_bag_opted_in,
  ]);

  useFocusEffect(
    useCallback(() => {
      if (showBagsLargeItemsModal) {
        fetchData();
      }
    }, [showBagsLargeItemsModal, fetchData]),
  );

  const ListFooterComponent = () => {
    return <View style={styles.footer} />;
  };

  return (
    <Modal
      statusBarTranslucent
      navigationBarTranslucent
      animationType="fade"
      transparent={true}
      visible={!!showBagsLargeItemsModal}
      onRequestClose={() => setShowBagsLargeItemsModal(false)}
    >
      <TouchableWithoutFeedback
        onPress={() => setShowBagsLargeItemsModal(false)}
      >
        <View style={styles.modalBackground} />
      </TouchableWithoutFeedback>
      <View style={[styles.contentContainer, { paddingBottom: insets.bottom }]}>
        <View style={styles.modalCross}>
          <Pressable onPress={() => setShowBagsLargeItemsModal(false)}>
            <Cross width={16} height={16} />
          </Pressable>
        </View>
        {loading ? (
          <View style={{ padding: 16 }}>
            <SkeletonLoaderList isLoading={loading} length={5} />
          </View>
        ) : (
          <>
            <View style={styles.headingContainer}>
              <Text variant="title_20">{"Bag & item list"}</Text>
            </View>
            <View style={styles.container}>
              <View style={{ marginBottom: 10 }}>
                <Text variant="body_text_2">{cxName}</Text>
                <Text variant="caption_2" style={{ color: colors.black06 }}>
                  {routeName}
                </Text>
              </View>
              <FlatList
                data={listItems}
                renderItem={({ item }) => renderItem(item)}
                keyExtractor={(item, index) => index.toString()}
                showsVerticalScrollIndicator={false}
                ListFooterComponent={() => <ListFooterComponent />}
              />
            </View>
          </>
        )}
      </View>
    </Modal>
  );
};

export default BagsItemsModal;

const styles = StyleSheet.create({
  modalBackground: {
    flex: 1,
    backgroundColor: colors.mediumBlacklightBlack,
    justifyContent: "flex-end",
    alignItems: "center",
  },
  contentContainer: {
    backgroundColor: "#fff",
    borderTopLeftRadius: 16,
    borderTopRightRadius: 16,
    width: "100%",
    position: "absolute",
    bottom: 0,
    minHeight: "50%",
    maxHeight: "70%",
  },

  headingContainer: {
    flexDirection: "row",
    justifyContent: "space-between",
    padding: 16,
    paddingTop: 20,
    alignItems: "center",
    borderBottomColor: colors.black02,
    borderBottomWidth: 0.5,
  },

  modalCross: {
    position: "absolute",
    right: 10,
    top: -35,
    backgroundColor: "#fff",
    borderRadius: 50,
    zIndex: 1,
    padding: 6,
  },
  container: {
    // flex: 1,
    padding: 16,
  },
  itemContainer: {
    flexDirection: "row",
    borderBottomWidth: StyleSheet.hairlineWidth,
    paddingVertical: 20,
    borderBottomColor: "#e0e0e0",
    flex: 1,
  },
  plasticBagRow: {
    backgroundColor: "#FFF9E6",
    paddingHorizontal: 12,
    borderRadius: 8,
    marginBottom: 8,
    borderBottomWidth: 0,
  },
  plasticBagTitle: {
    color: "#B45309",
  },
  itemImage: {
    width: 75,
    height: 75,
  },
  detailsContainer: {
    marginLeft: 16,
    flex: 1,
    justifyContent: "center",
  },
  itemName: {
    color: colors.mediumBlack,
  },
  itemQuantity: {
    color: colors.black06,
  },
  imageContainer: {
    borderRadius: 8,
    borderColor: colors.lighterBlack,
    borderWidth: 1,
    padding: 6,
    height: 85,
    width: 85,
    justifyContent: "center",
    alignItems: "center",
  },

  footer: {
    height: 100,
  },
});
